import { Box, Typography, useTheme } from '@mui/material';
import * as echarts from 'echarts';
import { ReactElement, useEffect, useMemo, useRef } from 'react';
import { WithListContext, useStore } from 'react-admin';
import { supportedDateFormats } from '../../../../Components/TzDate/utils';
import type { ActionLogsByUsersResource } from '../../../../Entities/ActionLogsByUser';
import { isNonEmptyString } from '../../../../utils';
import { formatActionLogReportDate } from './utils';

interface ChartProps {
  data: ActionLogsByUsersResource[];
}

function Chart({ data }: ChartProps): ReactElement {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  const [dateFormat] = useStore('format.date', supportedDateFormats[0].id);

  const { dates, series } = useMemo(() => {
    const dateValues = [
      ...new Set(data.map((record) => record.attributes?.date).filter(isNonEmptyString)),
    ].sort();
    const dates = dateValues.map((value) => formatActionLogReportDate(value, dateFormat));
    const recordsWithUserAndDate = data.flatMap((record) => {
      const {
        count,
        date,
        user_id: userId,
        user_identifier: userIdentifier,
      } = record.attributes ?? {};

      return isNonEmptyString(date) && isNonEmptyString(userId)
        ? [{ count, date, userId, userIdentifier }]
        : [];
    });
    const userRecords = Map.groupBy(recordsWithUserAndDate, (record) => record.userId);

    const series = [...userRecords.entries()]
      .sort(([leftUserId], [rightUserId]) => leftUserId.localeCompare(rightUserId))
      .map(([userId, records]) => {
        const countsByDate = new Map(records.map((record) => [record.date, record.count ?? 0]));
        const name =
          records.find((record) => isNonEmptyString(record.userIdentifier))?.userIdentifier ??
          userId;

        return {
          name,
          type: 'line',
          smooth: true,
          symbolSize: 5,
          data: dateValues.map((date) => countsByDate.get(date) ?? 0),
        };
      });

    return { dates, series };
  }, [data, dateFormat]);

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    const chart = echarts.init(chartRef.current, theme.palette.mode);
    chart.setOption({
      tooltip: {
        trigger: 'axis',
      },
      legend: {
        top: 0,
        type: 'scroll',
        textStyle: {
          color: theme.palette.text.primary,
        },
      },
      grid: {
        top: 56,
        right: 24,
        bottom: 80,
        left: 56,
      },
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: 0,
          filterMode: 'none',
          start: 0,
          end: 100,
        },
        {
          type: 'slider',
          xAxisIndex: 0,
          bottom: 16,
          height: 28,
          filterMode: 'none',
          start: 0,
          end: 100,
          textStyle: {
            color: theme.palette.text.secondary,
          },
        },
      ],
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: dates,
        axisLabel: {
          color: theme.palette.text.secondary,
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        axisLabel: {
          color: theme.palette.text.secondary,
        },
        splitLine: {
          lineStyle: {
            color: theme.palette.divider,
          },
        },
      },
      series,
    });

    const resizeObserver = new ResizeObserver(() => chart.resize());
    resizeObserver.observe(chartRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.dispose();
    };
  }, [dates, series, theme]);

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
      <Box ref={chartRef} sx={{ height: 360, width: '100%' }} />
    </Box>
  );
}

export default function ActionLogsByUserChart(): ReactElement {
  return (
    <WithListContext<ActionLogsByUsersResource>
      empty={<Typography sx={{ py: 2 }}>No chart data for this date range.</Typography>}
      render={({ data }) => (
        <Chart
          data={[...(data ?? [])].sort((left, right) => {
            const leftDate = left.attributes?.date ?? '';
            const rightDate = right.attributes?.date ?? '';

            if (leftDate === rightDate) {
              return (left.attributes?.user_id ?? '').localeCompare(
                right.attributes?.user_id ?? '',
              );
            }

            return leftDate.localeCompare(rightDate);
          })}
        />
      )}
    />
  );
}
